import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';
import { toast } from 'sonner';

import { quickClientApi } from '@client/api/waterlily';
import { selectSessionAdvisor, fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model';

export function useFillIntakeFormTrigger() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isFillIntakeFormOpen = searchParams.get('fillIntakeForm') === 'true';

  const openFillIntakeForm = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('fillIntakeForm', 'true');
      return newParams;
    });
  }, [setSearchParams]);

  const closeFillIntakeForm = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('fillIntakeForm');
      return newParams;
    });
  }, [setSearchParams]);

  return {
    isFillIntakeFormOpen,
    openFillIntakeForm,
    closeFillIntakeForm,
  };
}

export const fillIntakeFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

export type FillIntakeFormValues = z.infer<typeof fillIntakeFormSchema>;

export function useFillIntakeForm() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const { closeFillIntakeForm } = useFillIntakeFormTrigger();

  const form = useForm<FillIntakeFormValues>({
    resolver: zodResolver(fillIntakeFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  });

  const mutation = useMutation({
    mutationFn: quickClientApi.createQuickClientWithForm,
    onSuccess: data => {
      dispatch(
        fetchClientsForAdvisorByAdvisorIdRequest({
          advisorId: advisor.advisorId,
          isInvalidation: true,
        }),
      );
      dispatch({ type: 'RESUME_SESSION_REFRESH' });
      closeFillIntakeForm();
      form.reset();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${data.formReferenceId}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');
      toast.success('Client created successfully', {
        description: 'The intake form has been opened in a new tab.',
      });
    },
    onError: () => {
      toast.error('Failed to create client', {
        description: 'Please try again or contact support',
      });
    },
  });

  function onSubmit(data: FillIntakeFormValues) {
    mutation.mutate({
      advisorId: advisor.advisorId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
    });
  }

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: mutation.isPending,
  };
}
